/**
 * MentionTextarea — a plain <textarea> that supports @mention autocomplete.
 *
 * Storage format: mentions are stored as @[userId:DisplayName] tokens.
 *   e.g. "Hey @[3:Babu], please review this."
 *
 * The textarea itself shows a clean display version (@Name only, no ID).
 * onChange is called with the storage format containing @[id:name] tokens.
 *
 * Props:
 *   value          — controlled value (raw storage format with @[id:name] tokens)
 *   onChange(val)  — called with new raw value on every keystroke
 *   members        — [{ user_id, first_name, last_name, email }]
 *   placeholder    — textarea placeholder
 *   rows           — textarea rows (default 2)
 *   onKeyDown      — optional extra keydown handler (e.g. Ctrl+Enter to submit)
 *   disabled
 *   autoFocus
 *   style          — extra style for the textarea
 *   className
 *   maxLength
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { memberDisplayName } from '../../lib/format';

// Convert storage format "@[id:Name]" → display format "@Name"
function storageToDisplay(text) {
  return (text || '').replace(/@\[(\d+):([^\]]*)\]/g, '@$2');
}

// Convert storage format to display for the textarea, tracking cursor offset
// (used after programmatic inserts)

const URL_SPLIT_RE = /((?:https?:\/\/|www\.)[^\s<>"]+)/gi;
const URL_TEST_RE  = /^(?:https?:\/\/|www\.)/i;

// ── Utility: render stored text (with @[id:name] tokens) to JSX ───────────────
export function renderMentionText(text) {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {lines.map((line, li) => {
        const parts = line.split(/(@\[\d+:[^\]]*\])/g);
        let keyCounter = 0;
        const rendered = parts.flatMap((part) => {
          const m = part.match(/^@\[(\d+):([^\]]*)\]$/);
          if (m) return [<span key={keyCounter++} className="mention-chip">@{m[2]}</span>];
          // URLs first, so an "@" inside a link isn't turned into a mention chip
          return part.split(URL_SPLIT_RE).flatMap((seg) => {
            if (URL_TEST_RE.test(seg)) {
              // Keep trailing punctuation (e.g. "see https://x.com.") out of the link
              const [, url, trail] = seg.match(/^(.*?)([.,;:!?)\]'"]*)$/);
              const href = /^www\./i.test(url) ? `https://${url}` : url;
              return [
                <a key={keyCounter++} href={href} target="_blank" rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{ color: 'var(--primary)', textDecoration: 'underline', wordBreak: 'break-all' }}>{url}</a>,
                trail && <React.Fragment key={keyCounter++}>{trail}</React.Fragment>,
              ];
            }
            const subParts = seg.split(/(@[\w.]+)/g);
            return subParts.map((sub) => {
              if (/^@[\w.]+$/.test(sub)) return <span key={keyCounter++} className="mention-chip">{sub}</span>;
              return <React.Fragment key={keyCounter++}>{sub}</React.Fragment>;
            });
          });
        });
        return (
          <React.Fragment key={li}>
            {li > 0 && <br />}
            {rendered}
          </React.Fragment>
        );
      })}
    </span>
  );
}

// ── Utility: extract user IDs from stored text ────────────────────────────────
export function extractMentionIds(text) {
  if (!text) return [];
  const re = /@\[(\d+):[^\]]*\]/g;
  const ids = [];
  let m;
  while ((m = re.exec(text)) !== null) ids.push(parseInt(m[1], 10));
  return [...new Set(ids)];
}

// ── Main component ────────────────────────────────────────────────────────────
export default function MentionTextarea({
  value = '',
  onChange,
  members = [],
  placeholder = 'Write…',
  rows = 2,
  onKeyDown,
  disabled,
  autoFocus,
  style,
  className,
  maxLength,
  autoGrow = false,
  maxGrowHeight = 240,
}) {
  const ref        = useRef(null);
  const [dropdown, setDropdown] = useState(null);
  // dropdown = { query: string, start: number, results: [...], index: number }

  // Display state — what the textarea actually shows (no IDs)
  const [display, setDisplay] = useState(() => storageToDisplay(value));
  // Storage state — parallel string with @[id:name] tokens
  const storageRef = useRef(value);

  // Keep display in sync when parent resets value (e.g. after submit)
  useEffect(() => {
    storageRef.current = value;
    setDisplay(storageToDisplay(value));
  }, [value]);

  // Auto-grow: resize textarea height to fit content up to maxGrowHeight
  const growTextarea = useCallback(() => {
    if (!autoGrow || !ref.current) return;
    const el = ref.current;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, maxGrowHeight) + 'px';
  }, [autoGrow, maxGrowHeight]);

  useEffect(() => {
    if (autoGrow) growTextarea();
  }, [display, autoGrow, growTextarea]);

  // ── Detect @trigger on each keystroke ──────────────────────────────────────
  const handleChange = useCallback((e) => {
    const disp   = e.target.value;
    const cursor = e.target.selectionStart;

    // Sync storage with display edits.
    // When tokens exist in storage, we map the display edit back to storage
    // by finding the common prefix/suffix between old and new display and
    // applying the same edit region to storage (preserving tokens outside the edit).
    const prevDisplay = storageToDisplay(storageRef.current);
    if (prevDisplay === disp) {
      // no change
    } else if (!/@\[\d+:[^\]]*\]/.test(storageRef.current)) {
      // no tokens — storage == display
      storageRef.current = disp;
    } else {
      // Find common prefix length
      let prefixLen = 0;
      while (prefixLen < prevDisplay.length && prefixLen < disp.length && prevDisplay[prefixLen] === disp[prefixLen]) prefixLen++;
      // Find common suffix length (from end, not overlapping prefix)
      let suffixLen = 0;
      while (
        suffixLen < prevDisplay.length - prefixLen &&
        suffixLen < disp.length - prefixLen &&
        prevDisplay[prevDisplay.length - 1 - suffixLen] === disp[disp.length - 1 - suffixLen]
      ) suffixLen++;
      // The inserted text in the new display
      const insertedDisplay = disp.slice(prefixLen, disp.length - suffixLen);
      // Find corresponding region in storage (same prefix/suffix in storage display)
      const storageDisplay = prevDisplay; // same as storageToDisplay(storageRef.current)
      const storageEnd = storageRef.current.length - suffixLen;
      // Map prefix in display to prefix in storage character by character
      let storagePrefixLen = 0;
      let dispCount = 0;
      const storageStr = storageRef.current;
      while (storagePrefixLen < storageStr.length && dispCount < prefixLen) {
        const tokenMatch = storageStr.slice(storagePrefixLen).match(/^@\[(\d+):([^\]]*)\]/);
        if (tokenMatch) {
          const tokenDisplayLen = 1 + tokenMatch[2].length; // @Name
          if (dispCount + tokenDisplayLen > prefixLen) break;
          dispCount += tokenDisplayLen;
          storagePrefixLen += tokenMatch[0].length;
        } else {
          dispCount++;
          storagePrefixLen++;
        }
      }
      // Map suffix in display to suffix in storage
      let storageSuffixLen = 0;
      let dispSufCount = 0;
      while (storageSuffixLen < storageStr.length - storagePrefixLen && dispSufCount < suffixLen) {
        const pos = storageStr.length - 1 - storageSuffixLen;
        // Check if we're at the end of a token
        const tokenEndMatch = storageStr.slice(0, pos + 1).match(/@\[(\d+):([^\]]*)\]$/);
        if (tokenEndMatch) {
          const tokenDisplayLen = 1 + tokenEndMatch[2].length;
          if (dispSufCount + tokenDisplayLen > suffixLen) break;
          dispSufCount += tokenDisplayLen;
          storageSuffixLen += tokenEndMatch[0].length;
        } else {
          dispSufCount++;
          storageSuffixLen++;
        }
      }
      const storagePrefix = storageStr.slice(0, storagePrefixLen);
      const storageSuffix = storageSuffixLen > 0 ? storageStr.slice(storageStr.length - storageSuffixLen) : '';
      storageRef.current = storagePrefix + insertedDisplay + storageSuffix;
    }
    setDisplay(disp);
    onChange?.(storageRef.current);

    // Look backwards from cursor for an @word trigger
    const before = disp.slice(0, cursor);
    const match  = before.match(/(?:^|[\s\n])(@\w*)$/);

    if (match) {
      const query    = match[1].slice(1).toLowerCase();
      const startPos = cursor - match[1].length;
      const results  = members.filter((m) => {
        const name = memberDisplayName(m.first_name, m.last_name, m.email).toLowerCase();
        return name.includes(query) || (m.email || '').toLowerCase().includes(query);
      }).slice(0, 20);
      setDropdown({ query, start: startPos, results, index: 0 });
    } else {
      setDropdown(null);
    }
  }, [members, onChange]);

  // ── Insert selected mention ────────────────────────────────────────────────
  const insertMention = useCallback((member) => {
    if (!ref.current) return;
    const disp   = ref.current.value;
    const cursor = ref.current.selectionStart;
    const before = disp.slice(0, cursor);

    const match = before.match(/(?:^|[\s\n])(@\w*)$/);
    if (!match) { setDropdown(null); return; }

    const triggerLen = match[1].length;
    const atStart    = cursor - triggerLen;

    const name        = memberDisplayName(member.first_name, member.last_name, member.email);
    const displayToken = `@${name}`;
    const storageToken = `@[${member.user_id}:${name}]`;

    const newDisplay = disp.slice(0, atStart) + displayToken + ' ' + disp.slice(cursor);
    const newStorage = storageRef.current.slice(0, atStart) + storageToken + ' ' + storageRef.current.slice(cursor);

    storageRef.current = newStorage;
    setDisplay(newDisplay);
    onChange?.(newStorage);
    setDropdown(null);

    // Restore cursor after display token + space
    requestAnimationFrame(() => {
      if (ref.current) {
        const pos = atStart + displayToken.length + 1;
        ref.current.setSelectionRange(pos, pos);
        ref.current.focus();
      }
    });
  }, [onChange]);

  // ── Keyboard nav in dropdown ───────────────────────────────────────────────
  const handleKeyDown = useCallback((e) => {
    if (dropdown && dropdown.results.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setDropdown(d => ({ ...d, index: (d.index + 1) % d.results.length }));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setDropdown(d => ({ ...d, index: (d.index - 1 + d.results.length) % d.results.length }));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const sel = dropdown.results[dropdown.index];
        if (sel) { e.preventDefault(); insertMention(sel); return; }
      }
      if (e.key === 'Escape') {
        setDropdown(null);
        return;
      }
    }
    onKeyDown?.(e);
  }, [dropdown, insertMention, onKeyDown]);

  // Block image/file pastes — only allow plain text
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.kind === 'file') {
        e.preventDefault();
        return;
      }
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const close = (e) => {
      if (!ref.current?.contains(e.target)) setDropdown(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="mention-wrap" style={{ position: 'relative' }}>
      <textarea
        ref={ref}
        value={display}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        autoFocus={autoFocus}
        maxLength={maxLength}
        style={style}
        className={className}
      />

      {/* Mention dropdown */}
      {dropdown && dropdown.results.length > 0 && (
        <ul className="mention-dropdown">
          {dropdown.results.map((m, i) => {
            const name = memberDisplayName(m.first_name, m.last_name, m.email);
            const ini  = name.slice(0, 1).toUpperCase();
            return (
              <li
                key={m.user_id}
                className={`mention-option${i === dropdown.index ? ' active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
              >
                <span className="mention-avatar">{ini}</span>
                <span className="mention-name">{name}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
